// mantine
import { Button, Textarea, UnstyledButton } from '@mantine/core';
// icons
import { RefreshCcw, SendHorizontal } from 'lucide-react';
// assets
import { KeyboardEvent, SyntheticEvent, useState } from 'react';
// utils
import { useSendUserChatMessageMutation } from '@/services/chat/chat.queries.ts';
import { IChatMessage } from '@/modules/Chat/Chat.types.ts';
// assets
import styles from '@/modules/Chat/components/Footer/Footer.module.css';

interface FooterProps {
    userActorId?: string;
    onSend?: (payload: IChatMessage) => void;
    onChange?: () => void;
    isFetching: boolean;
    isDisabled?: boolean;
    disabledMessage?: string;
    refetch: () => void;
}

const Footer = ({
    userActorId,
    onSend,
    onChange,
    isFetching,
    isDisabled,
    disabledMessage = "To start conversation, please select a user's actor",
    refetch
}: FooterProps) => {
    const mutation = useSendUserChatMessageMutation();

    const [message, setMessage] = useState('');
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const sendMessage = () => {
        if (userActorId && message) {
            mutation.mutate({
                actorId: userActorId,
                data: {
                    text: message,
                    arguments: []
                }
            });
            setMessage('');
            onSend?.({
                content: message,
                is_agent: false,
                datetime: new Date().toISOString()
            });
            onChange?.();
        }
    };

    const handleSubmit = (event: SyntheticEvent) => {
        event.preventDefault();
        sendMessage();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.shiftKey)) {
            setMessage(`${message}\n`);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            sendMessage();
        }
    };

    return (
        <footer className={styles.footer}>
            <div className="flex items-center justify-between">
                <Button
                    className={styles.refresh}
                    color="gray"
                    leftSection={<RefreshCcw />}
                    loading={isFetching}
                    size="xs"
                    variant="transparent"
                    onClick={() => {
                        refetch();
                    }}
                >
                    Refresh Chat
                </Button>
            </div>
            <Textarea
                className="mb-3"
                disabled={isDisabled}
                placeholder="Write a message..."
                rows={4}
                value={message}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
            />
            <div className="flex gap-3 align-middle">
                {isDisabled ? (
                    <div className="text-xs text-red-6">{disabledMessage}</div>
                ) : undefined}
                <UnstyledButton
                    className={styles.sendButton}
                    type="submit"
                    onClick={handleSubmit}
                >
                    <SendHorizontal className="text-lg" />
                </UnstyledButton>
            </div>
        </footer>
    );
};

export default Footer;
